<template>
  <Popover>
    <Float
      v-if="user"
      placement="top-start"
      transition-name="slide-fade-from-bottom"
    >
      <PopoverButton
        class="dropdown-toggle group block w-full flex-shrink-0 focus:outline-none"
      >
        <div class="flex items-center">
          <div>
            <UserAvatar :user-id="user.id" hide-status />
          </div>
          <div class="ml-3 hidden @3xs/menu:block">
            <p
              v-show="!collapsedMenu"
              class="truncate font-bold text-white"
              :class="{
                'text-sm': user.full_name?.length >= 25,
                'text-base': user.full_name?.length >= 15
              }"
            >
              {{ user.full_name }}
            </p>
          </div>
        </div>
      </PopoverButton>

      <PopoverPanel v-slot="{ close }" class="z-10 mb-3 w-56 px-4 sm:px-0">
        <div class="box dark:bg-gray-700 dark:text-slate-200">
          <div
            class="flex flex-col gap-0.5 border-b border-black/5 p-4 dark:border-slate-700"
          >
            <div class="font-medium">
              {{ user.full_name }}
            </div>
            <div class="truncate text-xs text-gray-600 dark:text-gray-300">
              {{ user.email }}
            </div>
            <div class="text-xs text-gray-600 dark:text-gray-300">
              {{ $t('global.version') }}: {{ releaseVersion }}
            </div>
            <div
              class="capitalize-first -mx-1 cursor-pointer rounded px-1 text-xs text-sky-600 hover:bg-gray-200 dark:hover:bg-gray-600"
              @click="
                $router.push({ name: 'settings_version_note_index_route' })
              "
            >
              {{ $t('global.see_all_version_note') }}
            </div>
          </div>

          <div class="p-2">
            <RouterLink
              :to="{ name: 'profile_form_route' }"
              class="capitalize-first block flex items-center rounded-md p-2 transition-opacity duration-300 ease-in-out hover:bg-gray-200 dark:hover:bg-slate-400"
            >
              <FontAwesomeIcon :icon="['fal', 'user']" class="mr-2" />
              <div class="capitalize-first inline-block">
                {{ $t(`global.my_profile`) }}
              </div>
            </RouterLink>
          </div>

          <div
            class="flex flex-row items-center rounded-md border-t border-black/5 p-2 dark:bg-slate-700"
          >
            <div class="mx-2 flex flex-1 flex-row items-center gap-2">
              <h2 class="flex shrink-0 flex-row justify-center">
                <FontAwesomeIcon fixed-width :icon="['fal', 'swatchbook']" />
              </h2>
              <RouterLink
                :to="{ name: 'profile_theme_route' }"
                class="relative flex flex-1 items-center gap-2 rounded-lg border bg-white py-1 pr-10 pl-2 text-left sm:text-sm dark:bg-slate-600 dark:text-gray-200"
              >
                <span
                  :data-theme="selectedTheme?.id"
                  class="from-primary-400 to-primary-600 inline-block h-3 w-3 shrink-0 rounded bg-gradient-to-br"
                >
                  &nbsp;
                </span>
                <span class="block truncate text-left capitalize">
                  {{ selectedTheme?.name }}
                </span>
                <span
                  class="pointer-events-none absolute inset-y-0 right-2 flex items-center"
                >
                  <FontAwesomeIcon :icon="['fal', 'chevron-down']" />
                </span>
              </RouterLink>
            </div>
          </div>

          <div
            class="flex flex-row items-center rounded-md border-t border-black/5 p-2 dark:bg-slate-700"
          >
            <div class="mx-2 flex flex-1 flex-row items-center gap-2">
              <h2 class="flex shrink-0 flex-row justify-center">❄️</h2>
              <div class="flex-1">
                <AnimationSelector />
              </div>
            </div>
          </div>

          <div
            v-if="$can('manage_dossier_form')"
            class="border-t border-black/5 p-2 dark:border-slate-700"
          >
            <button
              class="flex w-full items-center gap-2 rounded-md p-2 transition-opacity duration-300 ease-in-out hover:bg-gray-200 dark:hover:bg-slate-400"
              @click="() => toggleGlobalForm(close)"
            >
              <FontAwesomeIcon :icon="['fal', 'list-check']" class="mr-2" />
              <div class="capitalize-first inline-block">
                {{ $t('global.form_editor') }}
              </div>
            </button>
          </div>
          <div class="border-t border-black/5 p-2 dark:border-slate-700">
            <TheDarkModeSwitcher2 />
          </div>
          <div class="border-t border-black/5 p-2 dark:border-slate-700">
            <a
              href=""
              class="flex items-center gap-2 rounded-md p-2 transition-opacity duration-300 ease-in-out hover:bg-gray-200 dark:hover:bg-slate-400"
              @click.prevent="() => toggleOpenModalQRCode(close)"
            >
              <FontAwesomeIcon :icon="['fal', 'qrcode']" fixed-width />
              <span>{{ $t('global.mobile_connection') }}</span>
            </a>
          </div>
          <div class="border-t border-black/5 p-2 dark:border-slate-700">
            <a
              href=""
              class="flex items-center gap-2 rounded-md p-2 transition-opacity duration-300 ease-in-out hover:bg-gray-200 dark:hover:bg-slate-400"
              @click.prevent="handleLogout"
            >
              <FontAwesomeIcon fixed-width :icon="['fal', 'sign-out']" />
              <span>{{ $t('global.user_logout') }}</span>
            </a>
          </div>
        </div>
      </PopoverPanel>
    </Float>
  </Popover>

  <AuthQRCodeGeneratorComponent
    v-model:opened="qrcodeModalOpened"
    @action:close="() => toggleOpenModalQRCode(close)"
  />
</template>
<script>
import TheDarkModeSwitcher2 from '@/views/layouts/dark-mode-switcher/TheDarkModeSwitcher2'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Float } from '@headlessui-float/vue'
import UserAvatar from '@c/Avatar/UserAvatar.vue'
import AuthQRCodeGeneratorComponent from '@c/Auth/AuthQRCodeGeneratorComponent.vue'
import AnimationSelector from '@c/AnimationSelector.vue'

export default {
  name: 'InfoUserModalComponent',
  components: {
    AnimationSelector,
    AuthQRCodeGeneratorComponent,
    UserAvatar,
    Float,
    FontAwesomeIcon,
    TheDarkModeSwitcher2,
    Popover,
    PopoverButton,
    PopoverPanel
  },
  data() {
    return {
      qrcodeModalOpened: false
    }
  },
  computed: {
    ...mapGetters({
      user: 'auth/getUser',
      releaseVersion: 'metadata/getReleaseVersion',
      listThemes: 'metadata/getGuiThemes'
    }),
    ...mapGetters('sideMenu', ['collapsedMenu']),
    selectedTheme() {
      const themeId = this.user?.settings?.gui_settings?.theme
      return this.listThemes.find((item) => item.id === themeId) ?? null
    }
  },
  methods: {
    ...mapActions({
      fetchMetadata: 'metadata/fetchAllMetadata'
    }),
    ...mapMutations({
      toggleGlobalFormEditionMode: 'metadata/toggleGlobalFormEditionMode'
    }),
    toggleGlobalForm(close) {
      this.toggleGlobalFormEditionMode()
      close()
    },
    refreshAuthHandler() {
      this.$store.dispatch('auth/refreshAuth')
    },
    async handleLogout() {
      await this.$store.dispatch('auth/logout')
    },
    toggleOpenModalQRCode(close) {
      this.qrcodeModalOpened = !this.qrcodeModalOpened

      if (this.qrcodeModalOpened) {
        close()
      }
    }
  }
}
</script>

<style>
.slide-fade-from-bottom-enter-active {
  transition: all 0.5s ease-out;
}

.slide-fade-from-bottom-leave-active {
  transition: all 0.3s ease-in;
}

.slide-fade-from-bottom-enter-from,
.slide-fade-from-bottom-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
